/**
 * Machine-readable refusal codes for FIELD VALIDATION on the registry write
 * routes (T699).
 *
 * ⭐ WHY THIS EXISTS, IN ONE SENTENCE: `isCanonicalAccountText` refuses a value
 * for two independent reasons — it is too long, or it carries a character that
 * cannot be stored — and three routes used to report BOTH with the sentence
 * "Field too long: name (120 characters max)". For a charset refusal that is
 * simply false, and the operator never even sees it (SEC-5), so what reached her
 * was a generic "Check the details". The offending character is invisible in
 * every case that matters and re-pasting reproduces it exactly, so nothing told
 * her what was wrong. The plausible next move is to retype the name slightly
 * differently until it takes — which is how `docs/pricing-spec.md` records this
 * business ending up with "the same club spelled up to four ways", one place
 * split across several rate keys.
 *
 * ⛔ THIS IS NOT A SEC-5 EXCEPTION, AND THE DISTINCTION MATTERS. The client does
 * NOT render the server's sentence. It reads this CODE off the body and picks
 * its own vetted copy, which is exactly what client/lib/api.ts documents
 * `ApiError.body` for ("Used to KEY friendly-copy maps") and what that file
 * means by "SEC-5 IS A RULE ABOUT THE DEFAULT, NOT AN ABSOLUTE". The one shipped
 * RAW-body exception is actions.ts's 403 arm; this is not a second one.
 *
 * ⛔ SHARED RATHER THAN RE-DECLARED, unlike the folds the admin screens copy.
 * Those copy because the repo module drags `pg` into the browser bundle and a
 * disagreeing fold can only cost a round trip. A disagreeing CODE is different:
 * a client that does not recognise what the server sent falls back to the
 * generic sentence and silently reinstates the defect. So there is one list, and
 * `isRegistryErrorCode` guards the boundary — mirroring send-schema.ts, which
 * settled this shape first.
 *
 * ⭐ THIS MODULE IS THE HOME FOR FIELD-VALIDATION CODES. EXTEND IT, DO NOT FORK
 * IT. Three further `Field too long` sites exist today (server/app.ts's booking
 * note, the golf-course name, and the registry note) which would reasonably want
 * `field_too_long`. They are NOT changed by T699 — but when one of them is, its
 * code belongs here. A second module named for whichever route needed it next is
 * how two lists of the same thing start disagreeing.
 *
 * ⛔ NEVER NAME A CODE CONTAINING `destinations_`, `CONSTRAINT`, `SELECT` OR
 * `INSERT`. `test/server/destination-write-routes.spec.ts`'s S6 assertion greps
 * the WHOLE serialised body for those, to prove no raw exception text escapes;
 * a code carrying one would fail it for a reason that has nothing to do with
 * what the test is about.
 */

export const REGISTRY_ERROR_CODES = [
  /** Longer than the field's bound, measured on the FOLDED value. */
  'field_too_long',
  /**
   * Carries a character that cannot be stored — soft hyphen, ZWSP, ZWNJ, word
   * joiner, RLO, middle dot, the C0/C1 controls.
   *
   * ⚠️ THE COPY KEYED OFF THIS MUST NOT SAY "INVISIBLE". U+00B7 MIDDLE DOT is in
   * the refused set and is perfectly visible, and so are the compatibility
   * ligatures. Say that the character cannot be saved and that typing it by hand
   * fixes it; do not name a property that is false for part of the set.
   */
  'field_charset',
  /**
   * ⭐ T1719 M3 — THE FIRST TWO 409 CODES IN THIS LIST, and they are here because
   * a status alone could not tell the operator what to do.
   *
   * `client/lib/account-actions.ts`'s 409 arm keyed on the STATUS only, so BOTH
   * short-code refusals were replaced by "There is already an account with that
   * name" — the exact outcome the server's branch ORDERING exists to prevent,
   * defeated one layer up. Worse, `AccountList.tsx` runs a client-side duplicate
   * NAME pre-check immediately before the POST, so the operator was told the name
   * clashed one keystroke after the list proved it did not.
   * ⇒ the code is what lets the screen say the true thing. Same design as the two
   * 400 codes above: the CODE crosses the wire, the SENTENCE is the client's.
   */
  /** Another account already holds that short code. `accounts_short_code_key`
   *  refused it (a total unique index); the remedy is different letters. */
  'short_code_conflict',
  /** DD4: a reference has already been issued under this code
   *  (`accounts.next_job_seq > 1`), so the letters are frozen. The remedy is not
   *  retyping - it is asking Alex, which only a code-keyed sentence can say. */
  'short_code_locked',
  /**
   * ⭐ T1719 M4 — THE FIRST CODE ON THIS LIST THAT IS NOT ABOUT WHAT WAS TYPED.
   * Postgres refused to WAIT: `55P03 lock_not_available`, raised because
   * `core/db.ts` sets `lock_timeout: 3000` on the app pool and another
   * transaction is holding an account lock.
   * ⚠️ **AN** ACCOUNT LOCK, NOT NECESSARILY THIS ACCOUNT'S ROW — the copy keyed
   * off this code must not claim otherwise. `lock_timeout` bounds the wait on
   * the GLOBAL `ACCOUNT_WRITE_LOCK_KEY` as well (measured at T1719 M4b: a
   * `pg_advisory_xact_lock` wait cancelled at 3.06 s as 55P03), so a save on a
   * completely quiet account raises it while some other account's save holds the
   * single key. ⛔ It is also raised at the two BOOKING doors since M4b.
   *
   * ⛔ IT NEEDS ITS OWN CODE PRECISELY BECAUSE THE REMEDY IS THE OPPOSITE OF
   * EVERY OTHER REFUSAL HERE. The two above say "change what you typed"; this one
   * says "change NOTHING and press save again". Reported as a duplicate name or a
   * bad field, an operator edits a value that was never wrong — which is exactly
   * the failure M3's D2 measured when both 409s shared the status-keyed arm.
   * ⚠️ TRANSIENT BY CONSTRUCTION: nothing was written, the transaction rolled
   * back, and the contending holder is bounded by `statement_timeout`.
   */
  'account_busy',
  /**
   * ⭐ T1719 M6 — THE THREE 400s THE M3 DOCBLOCK PROMISED M5 WOULD BRING AND M5
   * DID NOT. They are `readAccountWrite`'s short-code refusals, and until now
   * they crossed the wire with NO code at all, so `account-actions.ts`'s 400 arm
   * fell back to "Check the details. An account needs a name, and each line has
   * to be short enough to fit on a booking." - a sentence that never mentions
   * the short code, in front of an operator staring at the short-code box.
   *
   * ⛔ REACHABLE SINCE M5, WHICH IS WHAT MOVED THEM FROM "SCOPE STATEMENT" TO
   * "DEFECT". The Add form carries the field, so typing `ECM`, `WARD`, `HIV` or
   * a lowercase `ab` on a name with no clash reaches each of these; the screen's
   * own `codeClash` pre-check is about a code ANOTHER account holds and does not
   * intercept any of the three.
   *
   * ⚠️ THEY NAME THE RULE, NOT THE CHARACTER. `shortCodeRefusal` (the repo) is
   * the authority on WHICH rule refused; the sentence keyed off each code is the
   * client's, in both exhaustive maps. Same design as every code above: the CODE
   * crosses the wire, the SENTENCE is the client's (SEC-5).
   */
  /** The value is not two to five A-Z capitals - wrong length, wrong case, or a
   *  character outside the alphabet. ⛔ NEVER FOLDED: M1's accepted risk rests
   *  on the code being stored exactly as typed, so this refuses rather than
   *  upper-cases. */
  'short_code_shape',
  /** The value is on the clinical/resident/room/ward denylist. The reference is
   *  printed on the invoice the home receives, so a code that reads as a ward or
   *  a diagnosis would put it on a document a family can see. */
  'short_code_clinical',
  /** The value is `ECM`, the token the random reference form uses (DD4). Kept so
   *  no account-coded reference can be mistaken for a machine-minted one. */
  'short_code_reserved',
] as const;

export type RegistryErrorCode = (typeof REGISTRY_ERROR_CODES)[number];

/** Boundary guard: the client reads `code` off an untrusted response body. */
export function isRegistryErrorCode(v: unknown): v is RegistryErrorCode {
  return typeof v === 'string' && (REGISTRY_ERROR_CODES as readonly string[]).includes(v);
}
